import React, { useEffect, useMemo, useState } from "react";
import api from "../../../utils/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowRight, Bookmark, RefreshCw } from "lucide-react";
import { BASE_URL, questionsAPI, objectivequizApi, objectivequizReportApi } from "../../../api/api";
import "./TestPanel.css";

const ObjectiveTestPanel = () => {
  const [questions, setQuestions] = useState([]);
  const [quizList, setQuizList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [loading, setLoading] = useState(true);

  // Retrieve student info from sessionStorage
  const studentSession = JSON.parse(sessionStorage.getItem("studentSession")) || {};
  const studentId = studentSession?.id;

useEffect(() => {
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [quizRes, questionRes] = await Promise.all([
        api.get(`${objectivequizApi}`),
        api.get(`${questionsAPI}`),
      ]);

      const quizArray = Array.isArray(quizRes?.data) ? quizRes.data : quizRes.data?.data ?? [];

      const validQuizzes = quizArray.filter(
        (q) => Array.isArray(q.test_question) && q.test_question.length > 0 && q.status
      );

      if (!validQuizzes.length) {
        toast.warn("No active objective quizzes found.");
        setQuestions([]);
        setQuizList([]);
        setLoading(false);
        return;
      }

      const allQuestions = Array.isArray(questionRes?.data) ? questionRes.data : questionRes.data?.data ?? [];

      // Attach quiz_id to each question
      const questionsWithQuizId = [];
      validQuizzes.forEach((quiz) => {
        quiz.test_question.forEach((qid) => {
          const question = allQuestions.find((q) => Number(q.id) === Number(qid));
          if (question) {
            questionsWithQuizId.push({
              ...question,
              quiz_id: quiz.id, // attach quiz_id here
            });
          }
        });
      });

      setQuizList(validQuizzes);
      setQuestions(questionsWithQuizId);
      setCurrentIndex(0);
    } catch (err) {
      console.error("Error fetching quizzes/questions:", err);
      toast.error("Failed to load quiz/questions.");
      setQuestions([]);
      setQuizList([]);
    } finally {
      setLoading(false);
    }
  };

  fetchAll();
}, []);

  const getOptionsFor = (q) => {
    if (!q) return [];
    const raw = [q.option_a, q.option_b, q.option_c, q.option_d];
    return raw
      .map((opt) => (opt === null || opt === undefined ? null : String(opt)))
      .filter(Boolean);
  };

  const currentQuestion = useMemo(
    () => questions[currentIndex],
    [questions, currentIndex]
  );

  const handleOptionChange = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const handleClear = () => {
    const qid = currentQuestion?.id;
    if (!qid) return;
    setAnswers((prev) => {
      const updated = { ...prev };
      delete updated[qid];
      return updated;
    });
    toast.info("Response cleared");
  };

  const handleMarkAndNext = () => {
    const qid = currentQuestion?.id;
    if (qid) setFlagged((prev) => ({ ...prev, [qid]: true }));
    toast.success("Marked and moving to next");
    moveNext();
  };

  const handleSaveAndNext = () => {
    const qid = currentQuestion?.id;
    if (qid && answers[qid]) toast.success("Saved answer and moving to next");
    else toast.info("No answer selected — moving to next");
    moveNext();
  };

  const moveNext = () => {
    setCurrentIndex((prev) => {
      if (prev < questions.length - 1) return prev + 1;
      toast.info("You have reached the last question");
      return prev;
    });
  };

  const movePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const goToQuestion = (i) => {
    if (i < 0 || i >= questions.length) return;
    setCurrentIndex(i);
  };

  const RenderHTML = ({ html }) => (
    <span dangerouslySetInnerHTML={{ __html: html ?? "" }} />
  );

  const handleSubmit = async () => {
  if (!questions.length) return;

  try {
    for (const q of questions) {
      if (!q.quiz_id) {
        console.error("Missing quiz_id for question", q);
        continue;
      }

      await api.post(`${objectivequizReportApi}`, {
        test_id: q.quiz_id,        //  Pass selected test_id dynamically
        student_id: studentId,     //  From sessionStorage
        question_id: q.id,         //  Question being answered
        marked_option: answers[q.id] || null, //  Marked option (null if unanswered)
        // status: answers[q.id] ? 1 : 0, // Optional: 1 = answered, 0 = skipped
      });
    }

    toast.success("Test submitted successfully!");
  } catch (err) {
    console.error("Error submitting test:", err.response?.data || err);
    toast.error("Failed to submit test");
  }
};




  return (
    <div className="mock-test-panel-container">

      <header className="mock-test-panel-header">
        <div className="mock-test-panel-header-inner">
          <div className="mock-test-panel-title">
            <h1>
              {quizList.length === 1
                ? quizList[0].test_name
                : quizList.length > 1
                  ? "Combined Objective Test"
                  : "Objective Test"}
            </h1>
            <p className="mock-test-panel-subtitle">
              {quizList.length === 1
                ? quizList[0].test_description
                : quizList.length > 1
                  ? `${quizList.length} quizzes combined`
                  : ""}
            </p>
          </div>

          <div className="mock-test-panel-meta">
            <div className="mock-test-panel-progress">
              <div className="mock-test-panel-progress-bar">
                <div
                  className="mock-test-panel-progress-fill"
                  style={{
                    width: questions.length
                      ? `${(Object.keys(answers).length / questions.length) * 100}%`
                      : "0%",
                  }}
                />
              </div>
              <small>
                {questions.length
                  ? `${Object.keys(answers).length}/${questions.length} answered`
                  : "0/0"}
              </small>
            </div>
          </div>
        </div>
      </header>

      <main className="mock-test-panel-body">
        <aside className="mock-test-panel-sidebar" aria-label="Question list">
          <h3 className="mock-test-panel-sidebar-title">Questions</h3>

          <div className="mock-test-panel-question-list">
            {loading ? (
              <div className="mock-test-panel-loading">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="mock-test-panel-empty">No questions available</div>
            ) : (
              questions.map((q, i) => {
                const answered = !!answers[q.id];
                const isFlagged = !!flagged[q.id];
                return (
                  <button
                    key={q.id}
                    onClick={() => goToQuestion(i)}
                    className={`mock-test-panel-qnav ${i === currentIndex ? "active" : ""
                      } ${answered ? "answered" : ""} ${isFlagged ? "flagged" : ""}`}
                    aria-label={`Question ${i + 1}${answered ? " answered" : ""}${isFlagged ? " flagged" : ""}`}
                    title={`Question ${i + 1}`}
                  >
                    <div className="mock-test-panel-qnum">{i + 1}</div>
                    <div className="mock-test-panel-qbadge">
                      {isFlagged ? <Bookmark size={12} /> : answered ? "✓" : ""}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="mock-test-panel-sidebar-actions">
            <button
              className="mock-test-panel-sidebar-prev"
              onClick={movePrev}
              disabled={currentIndex === 0}
            >
              Prev
            </button>
            <button
              className="mock-test-panel-sidebar-next"
              onClick={moveNext}
              disabled={currentIndex >= questions.length - 1}
            >
              Next
            </button>
          </div>
        </aside>

        <section className="mock-test-panel-question-area">
          {loading ? (
            <div className="mock-test-panel-loading-large">Loading...</div>
          ) : !currentQuestion ? (
            <div className="mock-test-panel-empty-large">No question to display</div>
          ) : (
            <article className="mock-test-panel-question-card">
              <header className="mock-test-panel-question-head">
                <div className="mock-test-panel-qmeta">
                  <span className="mock-test-panel-subject-name">{currentQuestion.subject_name}</span>
                  <span className="mock-test-panel-topic-name">{currentQuestion.topic_name}</span>
                </div>
                <div className="mock-test-panel-qindex">
                  Q {currentIndex + 1} / {questions.length}
                </div>
              </header>

              <div className="mock-test-panel-qtext">
                <RenderHTML html={currentQuestion.question_text} />
              </div>

              <div className="mock-test-panel-options">
                {getOptionsFor(currentQuestion).map((opt, idx) => {
                  const qid = currentQuestion.id;
                  const checked = answers[qid] === opt;
                  return (
                    <label key={idx} className="mock-test-panel-option">
                      <input
                        type="radio"
                        name={`q-${qid}`}
                        value={opt}
                        checked={checked}
                        onChange={() => handleOptionChange(qid, opt)}
                      />
                      <div className="mock-test-panel-option-content">
                        <span className="mock-test-panel-option-label">{String.fromCharCode(65 + idx)}.</span>
                        <span className="mock-test-panel-option-text"><RenderHTML html={opt} /></span>
                      </div>
                    </label>
                  );
                })}
              </div>

              <footer className="mock-test-panel-actions">
                <button className="mock-test-panel-btn clear" onClick={handleClear}>
                  <RefreshCw size={16} /> Clear Response
                </button>

                <button className="mock-test-panel-btn mark" onClick={handleMarkAndNext}>
                  <Bookmark size={16} /> Mark & Next
                </button>

                <button className="mock-test-panel-btn save" onClick={handleSaveAndNext}>
                  Save & Next <ArrowRight size={16} />
                </button>

                <button className="mock-test-panel-btn submit" onClick={handleSubmit}>
                  Submit Test
                </button>
              </footer>
            </article>
          )}
        </section>
      </main>
    </div>
  );
};

export default ObjectiveTestPanel;
